import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { CompanyDetailsInterface } from '../../services/exports/Interfaces';
import useMaps from '../../hooks/location/useMaps';
import { GoogleMap, Marker } from '@react-google-maps/api';
import classnames from 'classnames';
import DeliveryZoneGeometry from './DeliveryZone';
import useGoogleMaps from '../../hooks/location/useGoogleMaps';

interface Props {
  data: CompanyDetailsInterface;
  className?: string;
}

export default function CompanyLocation(props: Props) {
  const { data, className } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Company:CompanyLocation' });
  const { generateEmbedMapUrl } = useMaps();

  // Both the embed iframe and the dynamic map contact Google, so neither loads until the customer
  // asks for the map (MR !490 review). Only the dynamic map needs the JS API.
  const [isMapRequested, setIsMapRequested] = useState<boolean>(false);
  const usesEmbedMap = !!data && !data.has_delivery && !data.has_multiple_locations && !!data.google_places_id;
  const {
    isLoaded: isGoogleMapsLoaded,
    loadError: googleMapsLoadError,
    retry: retryGoogleMaps,
  } = useGoogleMaps({ enabled: !!data && isMapRequested && !usesEmbedMap });

  const renderMap = () => {
    if (!data) {
      return undefined;
    }

    if (!isMapRequested) {
      return renderMapPlaceholder();
    }

    if (usesEmbedMap) {
      return renderEmbedMap();
    }

    return renderDynamicMap();
  };

  const renderEmbedMap = () => (
    <iframe
      src={generateEmbedMapUrl()}
      className="h-full w-full"
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
  );

  const renderMapPlaceholder = () => (
    <div data-test="company-map-placeholder" className={placeholderContainer}>
      <MapPin className="size-6 text-brand-primary" />
      <p className="text-xs">{t('text')}</p>
      <button
        type="button"
        data-test="company-map-show-button"
        onClick={() => setIsMapRequested(true)}
        className={placeholderButton}
      >
        {t('buttons.show_map')}
      </button>
    </div>
  );

  const renderDynamicMap = () => {
    if (googleMapsLoadError) {
      return (
        <div data-test="company-map-error" className={placeholderContainer}>
          <p className="text-xs">{t('errors.map_unavailable')}</p>
          <button
            type="button"
            data-test="company-map-retry-button"
            onClick={retryGoogleMaps}
            className={placeholderButton}
          >
            {t('buttons.retry')}
          </button>
        </div>
      );
    }

    return renderGoogleMap();
  };

  const renderGoogleMap = () =>
    isGoogleMapsLoaded &&
    data?.lat &&
    data?.lon && (
      <GoogleMap
        mapContainerClassName="h-full w-full"
        center={{
          lat: data?.lat,
          lng: data?.lon,
        }}
        zoom={12}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
        }}
      >
        {renderMarker()}
        {data?.delivery_zones?.map((item) => (
          <DeliveryZoneGeometry data={item} key={`delivery-zone-${item?.id}`} />
        ))}
      </GoogleMap>
    );

  const renderMarker = () => (
    <Marker
      label={data?.name}
      position={{
        lat: data?.lat,
        lng: data?.lon,
      }}
    />
  );

  return <div className={classnames('w-full', className)}>{renderMap()}</div>;
}

const placeholderContainer =
  'h-full w-full flex flex-col items-center justify-center gap-sm px-md text-center bg-brand-background-hover rounded-md';
const placeholderButton =
  'px-md py-xs rounded-md text-sm font-medium text-brand-button-primary-text bg-brand-button-primary-background';
